"use client";
import React, {
  useState,
  useMemo,
  useEffect,
  useCallback,
  useRef,
} from "react";
import { useCompatRouter as useRouter } from "@/lib/compat-router/context";
import { HomeWrapper } from "@/components/layouts";
import { containerWidth } from "@/components/layouts/utils";
import RealtorService from "@/services/realtor";
import { usePostHogAnalytics } from "@/contexts/PostHogAnalyticsContext";
import PropertyListingCard from "@/components/bnp-ui/listing-card/PropertyListingCard";
import PropertyCtaBanner from "@/components/shared/PropertyCtaBanner";
import { LoadMore, Breadcrumb } from "@/components/shared";
import {
  IconStar,
  IconPhone,
  IconMail,
  IconMapPin,
  IconCalendar,
  IconBuilding,
  IconArrowRight,
  IconLayoutGrid,
  IconHome,
  IconKey,
  IconMap,
  IconFileText,
  IconClock,
  IconShare,
  IconMessageCircle,
  IconX,
  IconCircleCheck,
  IconShieldCheck,
} from "@tabler/icons-react";
import {
  Drawer,
  Textarea,
  Button,
  Stack,
  Text,
  Avatar,
  Rating,
  Divider,
  Alert,
  Box,
} from "@mantine/core";
import useAuthToken from "@/hooks/useAuthToken";
import { Property } from "@/types/property";
import { categorizeProperty } from "@/utils/helpers/categorize-property";
import { buildPropertyDetailsUrl } from "@/utils/propertyReferrer";
import {
  mapApiVerifiedStatus,
  type RealtorVerificationStatus,
} from "@/types/property";

// --- Types ---
interface ApiRealtor {
  id: string;
  full_name: string;
  email: string;
  phone_number: string;
  is_partner: boolean;
  plan: string;
  join_date: string;
  profile_image_url?: string;
  business_name?: string;
  bio?: string;
  rating?: number;
  review_count?: number;
  listed_propeties_count?: number;
  verified?: boolean | RealtorVerificationStatus;
  state?: string;
  city?: string;
  address?: string;
  instagram?: string;
  twitter?: string;
  linkedin?: string;
  slug?: string;
}

interface Realtor {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  is_partner: boolean;
  plan: string;
  join_date: string;
  photo?: string | null;
  business_name?: string | null;
  bio?: string;
  rating?: number;
  review_count?: number;
  total_properties?: number;
  verified?: RealtorVerificationStatus;
  state?: string;
  city?: string;
  instagram?: string | null;
  twitter?: string | null;
  linkedin?: string | null;
  slug?: string | null;
}

interface StatCount {
  total: number;
  sale: number;
  rent: number;
  shortlet: number;
  lease: number;
  land: number;
}

interface RealtorProfilePageProps {
  initialRealtor: Realtor | null;
  initialProperties: Property[];
  initialTotalCount: number;
  initialStatCount: StatCount | null;
}

const SITE_ORIGIN =
  process.env.NEXT_PUBLIC_BASE_URL || "https://www.findnigeriaproperties.com";

const getAbsoluteUrl = (value?: string) => {
  if (!value) return `${SITE_ORIGIN}/logo.png`;
  if (value.startsWith("http://") || value.startsWith("https://")) return value;
  if (value.startsWith("//")) return `https:${value}`;
  if (value.startsWith("/")) return `${SITE_ORIGIN}${value}`;
  return `${SITE_ORIGIN}/${value}`;
};

const nullableString = (value?: string | null) => value ?? null;

const mapApiRealtorToUi = (data: ApiRealtor): Realtor => {
  const parts = data.full_name?.trim().split(" ") || [];
  const first_name = parts[0] || "";
  const last_name = parts.slice(1).join(" ") || "";
  return {
    id: data.id,
    first_name,
    last_name,
    email: data.email,
    phone: data.phone_number,
    is_partner: data.is_partner,
    plan: data.plan,
    join_date: data.join_date,
    photo: nullableString(data.profile_image_url),
    business_name: data.business_name || data.full_name || null,
    bio: data.bio || data.address || "",
    rating: data.rating ?? 0,
    review_count: data.review_count ?? 0,
    total_properties: data.listed_propeties_count ?? 0,
    verified: mapApiVerifiedStatus(data.verified),
    state: data.state || "",
    city: data.city || "",
    instagram: nullableString(data.instagram),
    twitter: nullableString(data.twitter),
    linkedin: nullableString(data.linkedin),
    slug: nullableString(data.slug),
  };
};

// FIX: previously this overwrote real API values (is_negotiable, created_at,
// is_saved, impression/save/inspection counts, serviced, shared, number_of_days,
// createdBy) with hardcoded fake defaults after already spreading the real
// property. Spreading `...property` already gives us the correct values for
// all of those fields, so we only need to coerce the numeric-looking fields
// to strings and null-guard caution_fee (String(null) used to render "null").
const mapApiPropertyToUi = (
  property: Property,
  realtorData: Realtor,
): Property =>
  ({
    ...property,
    reference: property.id,
    currency: "NGN",
    price: String(property.price),
    agency_fees: String(property.agency_fees),
    legal_fees: String(property.legal_fees),
    caution_fee:
      property.caution_fee != null ? String(property.caution_fee) : null,
    total_package: String(property.total_package),
  }) as unknown as Property;

// --- Helpers ---
const timeAgo = (dateString: string) => {
  const now = new Date();
  const date = new Date(dateString);
  const seconds = Math.floor((now.getTime() - date.getTime()) / 1000);
  const intervals = [
    { label: "year", seconds: 31536000 },
    { label: "month", seconds: 2592000 },
    { label: "week", seconds: 604800 },
    { label: "day", seconds: 86400 },
    { label: "hour", seconds: 3600 },
    { label: "minute", seconds: 60 },
  ];
  for (const interval of intervals) {
    const count = Math.floor(seconds / interval.seconds);
    if (count >= 1)
      return `${count} ${interval.label}${count > 1 ? "s" : ""} ago`;
  }
  return "Just now";
};

const formatJoinDate = (dateString: string) => {
  return new Date(dateString).toLocaleDateString("en-US", {
    month: "short",
    year: "numeric",
  });
};

const formatPhoneDisplay = (phone: string) => {
  const cleaned = phone.replace(/\s+/g, "");
  if (cleaned.length === 11)
    return `${cleaned.slice(0, 4)} ${cleaned.slice(4, 7)} ${cleaned.slice(7)}`;
  return phone;
};

const formatWhatsAppLink = (phone: string) => {
  const cleaned = phone.replace(/\s+/g, "");
  const formatted = cleaned.startsWith("0")
    ? "234" + cleaned.slice(1)
    : cleaned;
  return `https://wa.me/${formatted}`;
};

// --- WhatsApp Icon ---
const WhatsAppIcon = ({ className }: { className?: string }) => (
  <svg
    className={className}
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 24 24"
    fill="currentColor"
  >
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
  </svg>
);

// --- Category Config ---
const CATEGORIES = [
  { key: "all", label: "All Properties", Icon: IconLayoutGrid },
  { key: "sale", label: "For Sale", Icon: IconHome },
  { key: "rent", label: "For Rent", Icon: IconKey },
  { key: "land", label: "Land", Icon: IconMap },
  { key: "lease", label: "Lease", Icon: IconFileText },
  { key: "shortlet", label: "Shortlet", Icon: IconClock },
];

interface Review {
  id: string;
  author_name: string;
  author_avatar?: string;
  rating: number;
  comment: string;
  created_at: string;
}

// --- Review Drawer ---
const ReviewDrawer = ({
  realtorDataId,
  realtorDataName,
  opened,
  onClose,
}: {
  realtorDataId: string;
  realtorDataName: string;
  opened: boolean;
  onClose: () => void;
}) => {
  const { user, isLoading: authLoading } = useAuthToken();
  const { trackReviewSubmitted } = usePostHogAnalytics();
  const [reviews, setReviews] = useState<Review[]>([]);
  const [reviewsLoading, setReviewsLoading] = useState(false);
  const [reviewsError, setReviewsError] = useState<string | null>(null);
  const [newRating, setNewRating] = useState<number>(0);
  const [newComment, setNewComment] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  const fetchReviews = useCallback(async () => {
    if (!realtorDataId) return;
    setReviewsLoading(true);
    setReviewsError(null);
    try {
      const res = await RealtorService.getRealtorById(realtorDataId);
      console.log("Fetched reviews response:", res);
      const data = res?.data?.data || res?.data || null;
      const reviewsData = data?.reviews;
      if (Array.isArray(reviewsData)) {
        setReviews(reviewsData);
      } else {
        setReviews([]);
      }
    } catch (err: any) {
      console.error("Failed to fetch reviews:", err);
      setReviewsError("Unable to load reviews at this time.");
      setReviews([]);
    } finally {
      setReviewsLoading(false);
    }
  }, [realtorDataId]);

  useEffect(() => {
    if (opened) {
      fetchReviews();
    }
  }, [opened, fetchReviews]);

  const handleSubmitReview = async () => {
    if (!newRating || !newComment.trim() || isSubmitting) return;
    setIsSubmitting(true);
    try {
      const review: Review = {
        id: `rev-${Date.now()}`,
        author_name: user ? user.full_name : "Anonymous User",
        rating: newRating,
        comment: newComment.trim(),
        created_at: new Date().toISOString(),
      };
      setReviews((prev) => [review, ...prev]);
      trackReviewSubmitted({
        review_id: review.id,
        realtor_id: realtorDataId,
        rating: newRating,
        review_source: "realtor_profile",
        verified_interaction: false,
      });
      setNewRating(0);
      setNewComment("");
      setSubmitSuccess(true);
      setTimeout(() => setSubmitSuccess(false), 3000);
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderStars = (rating: number) => (
    <div className="flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((star) => (
        <IconStar
          key={star}
          className={`w-3.5 h-3.5 ${
            star <= rating ? "text-[#2D96A7] fill-[#2D96A7]" : "text-gray-200"
          }`}
        />
      ))}
    </div>
  );

  return (
    <Drawer
      opened={opened}
      onClose={onClose}
      title={
        <span className="font-semibold text-gray-900 text-lg">
          Reviews for {realtorDataName}
        </span>
      }
      position="right"
      size="md"
      styles={{
        body: {
          padding: "0 1.5rem 1.5rem",
          maxHeight: "calc(100vh - 60px)",
          overflow: "hidden",
        },
        header: { padding: "1rem 1.5rem", borderBottom: "1px solid #f0f0f0" },
        content: { display: "flex", flexDirection: "column", height: "100%" },
      }}
    >
      <Box style={{ overflowY: "auto", flex: 1, paddingRight: "4px" }}>
        <Stack gap="md" py="md">
          {reviewsLoading ? (
            <div className="space-y-4 py-4">
              {[1, 2, 3].map((i) => (
                <div key={i} className="flex gap-3">
                  <div className="w-10 h-10 rounded-full bg-gray-100 animate-pulse flex-shrink-0" />
                  <div className="flex-1 space-y-2">
                    <div className="h-4 w-32 bg-gray-100 rounded animate-pulse" />
                    <div className="h-3 w-24 bg-gray-100 rounded animate-pulse" />
                    <div className="h-3 w-full bg-gray-100 rounded animate-pulse" />
                    <div className="h-3 w-2/3 bg-gray-100 rounded animate-pulse" />
                  </div>
                </div>
              ))}
            </div>
          ) : reviewsError ? (
            <div className="text-center py-10">
              <IconMessageCircle className="w-10 h-10 text-gray-300 mx-auto mb-3" />
              <Text className="text-sm text-gray-500 mb-3">{reviewsError}</Text>
              <Button
                onClick={fetchReviews}
                variant="subtle"
                size="xs"
                styles={{
                  root: { color: "#2D96A7", fontWeight: 600 },
                }}
              >
                Try again
              </Button>
            </div>
          ) : reviews.length === 0 ? (
            <div className="text-center py-10">
              <IconMessageCircle className="w-10 h-10 text-gray-300 mx-auto mb-3" />
              <Text className="text-sm text-gray-400">
                No reviews yet. Be the first to leave one!
              </Text>
            </div>
          ) : (
            reviews.map((review, idx) => (
              <div key={review.id}>
                <div className="flex gap-3">
                  <Avatar
                    src={review.author_avatar}
                    radius="xl"
                    size="md"
                    className="flex-shrink-0"
                  >
                    {review.author_name[0]}
                  </Avatar>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between">
                      <div>
                        <Text className="font-semibold text-sm text-gray-900">
                          {review.author_name}
                        </Text>
                        <div className="flex items-center gap-2 mt-0.5">
                          {renderStars(review.rating)}
                          <Text className="text-xs text-gray-400">
                            {timeAgo(review.created_at)}
                          </Text>
                        </div>
                      </div>
                    </div>
                    <Text className="text-sm text-gray-500 mt-1.5 leading-relaxed">
                      {review.comment}
                    </Text>
                  </div>
                </div>
                {idx < reviews.length - 1 && <Divider className="mt-4 mb-2" />}
              </div>
            ))
          )}
        </Stack>
      </Box>

      {!authLoading && !user && (
        <Alert color="blue" className="mt-4 rounded-xl">
          <Text className="text-sm">Please sign in to leave a review.</Text>
        </Alert>
      )}

      {!authLoading && user && (
        <div className="mt-auto pt-4 border-t border-gray-100">
          {submitSuccess && (
            <Alert color="green" className="mb-3 rounded-xl">
              <Text className="text-sm font-medium">
                Review submitted successfully!
              </Text>
            </Alert>
          )}
          <Stack gap="xs">
            <Text className="text-sm font-semibold text-gray-700">
              Write a Review
            </Text>
            <Rating
              value={newRating}
              onChange={setNewRating}
              size="lg"
              color="#2D96A7"
              className="self-start"
            />
            <Textarea
              placeholder="Share your experience with this realtor..."
              value={newComment}
              onChange={(e) => setNewComment(e.currentTarget.value)}
              minRows={3}
              maxRows={5}
              className="mt-2"
              styles={{
                input: {
                  borderRadius: "12px",
                  borderColor: "#e5e7eb",
                  fontSize: "14px",
                },
              }}
            />
            <Button
              onClick={handleSubmitReview}
              disabled={!newRating || !newComment.trim() || isSubmitting}
              className="mt-2 self-end"
              styles={{
                root: {
                  backgroundColor: "#2D96A7",
                  padding: "0.5rem 1.5rem",
                  fontWeight: 600,
                  borderRadius: "10px",
                },
                label: { fontSize: "14px" },
              }}
            >
              {isSubmitting ? "Submitting..." : "Submit Review"}
            </Button>
          </Stack>
        </div>
      )}
    </Drawer>
  );
};

// --- Main Component ---
const RealtorProfilePage: React.FC<RealtorProfilePageProps> = ({
  initialRealtor,
  initialProperties,
  initialTotalCount,
  initialStatCount,
}) => {
  const router = useRouter();
  const { realtorid } = router.query;
  const [activeFilter, setActiveFilter] = useState("all");
  const [showContactModal, setShowContactModal] = useState(false);
  const [showReviewDrawer, setShowReviewDrawer] = useState(false);
  const [properties, setProperties] = useState<Property[]>(
    initialProperties || [],
  );
  const [loading, setLoading] = useState(false);
  const [propertiesError, setPropertiesError] = useState<string | null>(null);
  const [realtor, setRealtor] = useState<Realtor | null>(initialRealtor);
  const [realtorLoading, setRealtorLoading] = useState(false);
  const [realtorError, setRealtorError] = useState<string | null>(
    initialRealtor ? null : "Realtor not found.",
  );
  const realtorData = realtor;
  const isVerifiedRealtor = realtorData?.verified === "verified";
  const realtorRef = useRef<Realtor | null>(initialRealtor);
  const propertiesGridRef = useRef<HTMLDivElement | null>(null);
  const [pageNumber, setPageNumber] = useState(1);
  const [totalCount, setTotalCount] = useState(initialTotalCount || 0);
  const [statCount, setStatCount] = useState<{
    total: number;
    sale: number;
    rent: number;
    shortlet: number;
    lease: number;
    land: number;
  }>(
    initialStatCount || {
      total: 0,
      sale: 0,
      rent: 0,
      shortlet: 0,
      lease: 0,
      land: 0,
    },
  );

  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const pageSize = 12;

  const fetchRealtor = useCallback(async () => {
    if (!realtorid || typeof realtorid !== "string") return;
    setRealtorLoading(true);
    setRealtorError(null);
    try {
      const res = await RealtorService.getRealtorById(realtorid);
      // console.log(res)
      const data = res?.data?.data || res?.data || null;
      if (data) {
        const mapped = mapApiRealtorToUi(data);
        realtorRef.current = mapped;
        setRealtor(mapped);
      } else {
        realtorRef.current = null;
        setRealtor(null);
        setRealtorError("Realtor not found.");
      }
    } catch (err: any) {
      console.error("Failed to fetch realtor:", err);
      realtorRef.current = null;
      setRealtor(null);
      setRealtorError("Unable to load this realtor profile at this time.");
    } finally {
      setRealtorLoading(false);
    }
  }, [realtorid]);

  const fetchProperties = useCallback(
    async (page: number = 1, realtorId?: string) => {
      const effectiveRealtorId =
        realtorId ||
        realtorRef.current?.id ||
        (typeof realtorid === "string" ? realtorid : undefined);
      if (!effectiveRealtorId) return;
      setLoading(true);
      setPropertiesError(null);
      try {
        const propertiesRes = await RealtorService.getPropertiesByRealtor(
          effectiveRealtorId,
          {
            pageNumber: page,
            pageSize: pageSize,
          },
        );
        const propertiesData = propertiesRes?.data?.data || [];
        let categorized = propertiesData.map((property: any) =>
          categorizeProperty(property),
        );

        const parsed = Array.isArray(propertiesData) ? propertiesData : [];
        const mapped = parsed.map((p: Property) =>
          mapApiPropertyToUi(p, realtorRef.current as Realtor),
        );
        const total = propertiesRes?.data?.metadata?.totalCount || 0;
        setTotalCount(total);
        if (propertiesRes?.data?.statCount) {
          setStatCount(propertiesRes.data.statCount);
        }
        if (page === 1) {
          setProperties(mapped);
        } else {
          setProperties((prev) => [...prev, ...mapped]);
        }
      } catch (err: any) {
        console.error("Failed to fetch properties:", err);
        if (page === 1) {
          setProperties([]);
        }
        setPropertiesError("Unable to load properties at this time.");
      } finally {
        setLoading(false);
        setIsLoadingMore(false);
      }
    },
    [realtorid, pageSize],
  );

  const handleLoadMore = async () => {
    if (isLoadingMore) return;
    setIsLoadingMore(true);
    const nextPage = pageNumber + 1;
    setPageNumber(nextPage);
    await fetchProperties(nextPage);
  };

  useEffect(() => {
    if (initialRealtor) {
      realtorRef.current = initialRealtor;
    }
  }, [initialRealtor]);

  const loadedRef = useRef(false);

  useEffect(() => {
    if (loadedRef.current) return;
    if (!realtorid || typeof realtorid !== "string") return;
    loadedRef.current = true;
    let cancelled = false;

    const load = async () => {
      await fetchRealtor();
      if (!cancelled) {
        await fetchProperties(1, realtorRef.current?.id || undefined);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [realtorid, fetchRealtor, fetchProperties]);

  const availableCategories = useMemo(() => {
    const cats = new Set(properties.map((p) => p.listing_type));
    return CATEGORIES.filter((c) => c.key === "all" || cats.has(c.key));
  }, [properties]);

  const filteredProperties = useMemo(() => {
    if (activeFilter === "all") return properties;
    return properties.filter((p) => p.listing_type === activeFilter);
  }, [activeFilter, properties]);

  console.log(filteredProperties);

  const categoryCounts = useMemo(() => {
    const counts: Record<string, number> = {
      all: statCount.total || totalCount,
      sale: statCount.sale || 0,
      rent: statCount.rent || 0,
      shortlet: statCount.shortlet || 0,
      lease: statCount.lease || 0,
      land: statCount.land || 0,
    };
    return counts;
  }, [statCount, totalCount]);

  const realtorName = realtorData
    ? realtorData.business_name ||
      `${realtorData.first_name} ${realtorData.last_name}`
    : "";

  const seoTitle = realtorData
    ? `${realtorName} | Realtor on FindNigeriaProperties`
    : "Realtor Profile";
  const seoDescription = realtorData
    ? realtorData.bio ||
      `${realtorName} is a trusted realtor with ${totalCount} properties on FindNigeriaProperties.`
    : "View this realtor profile on FindNigeriaProperties.";
  const pageUrl = realtorData
    ? `${SITE_ORIGIN}/realtors/${realtorData.slug || realtorData.id}`
    : undefined;
  const shareImage = realtorData?.photo
    ? getAbsoluteUrl(realtorData.photo)
    : getAbsoluteUrl("/logo.png");

  const handleShare = async () => {
    if (!realtorData || !pageUrl) return;

    const slug = realtorData.slug || realtorData.id;
    const businessName = realtorData.business_name || realtorName;
    const location = realtorData.state || "";
    const propertyCount = totalCount || realtorData.total_properties || 0;

    const shareText =
      `Check out ${businessName} on Find Nigeria Properties\n\n` +
      `${propertyCount} propert${propertyCount === 1 ? "y" : "ies"} listed\n` +
      (location ? `Located in ${location}\n\n` : "\n") +
      `View my profile: ${pageUrl}`;

    const sharePayload = {
      title: businessName,
      text: shareText,
      url: pageUrl,
    };

    try {
      if (navigator.share) {
        await navigator.share(sharePayload);
        return;
      }
    } catch (error) {
      console.error("Native share failed:", error);
    }

    const fallbackUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(pageUrl)}`;
    window.open(
      fallbackUrl,
      "share-realtor",
      "width=600,height=500,scrollbars=yes,resizable=yes",
    );
  };

  if (router.isFallback || !realtorid) {
    return (
      <HomeWrapper showHero={false}>
        <div className={containerWidth}>
          <div className="min-h-screen flex items-center justify-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#2D96A7]" />
          </div>
        </div>
      </HomeWrapper>
    );
  }

  // Realtor profile loading skeleton
  if (realtorLoading) {
    return (
      <HomeWrapper showHero={false}>
        <div className={containerWidth}>
          <div className="min-h-screen bg-white mt-40">
            <div className="border-b border-gray-200 bg-white">
              <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
                <div className="h-4 w-64 bg-gray-100 rounded animate-pulse" />
              </div>
            </div>
            <section className="relative overflow-hidden bg-gradient-to-br from-[#2D96A7]/5 via-white to-white">
              <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 md:py-16">
                <div className="bg-white border border-gray-200 rounded-2xl shadow-sm overflow-hidden">
                  <div className="relative h-36 md:h-48 bg-gradient-to-r from-[#2D96A7]/20 via-[#2D96A7]/5 to-white" />
                  <div className="relative px-6 md:px-10 pb-8 -mt-16 md:-mt-20">
                    <div className="flex flex-col md:flex-row md:items-end gap-6">
                      <div className="w-28 h-28 md:w-36 md:h-36 rounded-2xl border-4 border-white overflow-hidden shadow-lg bg-gray-100 animate-pulse" />
                      <div className="flex-1 min-w-0 pb-1 space-y-3">
                        <div className="h-8 w-56 bg-gray-100 rounded animate-pulse" />
                        <div className="h-4 w-40 bg-gray-100 rounded animate-pulse" />
                        <div className="h-4 w-72 bg-gray-100 rounded animate-pulse" />
                      </div>
                      <div className="flex flex-col sm:flex-row gap-3 pb-1 flex-shrink-0">
                        <div className="h-11 w-32 bg-gray-100 rounded-xl animate-pulse" />
                        <div className="h-11 w-28 bg-gray-100 rounded-xl animate-pulse" />
                      </div>
                    </div>
                    <div className="mt-6 space-y-2">
                      <div className="h-4 w-full max-w-3xl bg-gray-100 rounded animate-pulse" />
                      <div className="h-4 w-2/3 max-w-3xl bg-gray-100 rounded animate-pulse" />
                    </div>
                  </div>
                </div>
              </div>
            </section>
            <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 md:py-16">
              <div className="h-8 w-72 bg-gray-100 rounded animate-pulse mb-8" />
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                {[1, 2, 3].map((i) => (
                  <div
                    key={i}
                    className="h-96 bg-gray-100 rounded-2xl animate-pulse"
                  />
                ))}
              </div>
            </section>
          </div>
        </div>
      </HomeWrapper>
    );
  }

  // Realtor profile error state
  if (realtorError || !realtorData) {
    return (
      <HomeWrapper showHero={false}>
        <div className={containerWidth}>
          <div className="min-h-screen bg-white mt-40 flex items-center justify-center px-4">
            <div className="text-center max-w-md">
              <IconBuilding className="w-14 h-14 text-gray-300 mx-auto mb-4" />
              <h1 className="text-xl font-semibold text-gray-800 mb-2">
                Realtor not found
              </h1>
              <p className="text-sm text-gray-500 mb-6">
                {realtorError || "This realtor profile could not be found."}
              </p>
              <button
                onClick={() => router.push("/realtors")}
                className="inline-flex items-center gap-2 px-6 py-3 bg-[#2D96A7] hover:bg-[#2D96A7]/90 text-white text-sm font-semibold rounded-xl transition-all duration-300"
              >
                Browse Realtors
                <IconArrowRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      </HomeWrapper>
    );
  }

  return (
    <>
      <HomeWrapper showHero={false}>
        <div className={containerWidth}>
          <div className="min-h-screen bg-white mt-40">
            {/* ===== BREADCRUMB ===== */}
            <Breadcrumb
              items={[
                { label: "Home", href: "/" },
                { label: "Realtors", href: "/realtors" },
                { label: `${realtorName}` },
              ]}
            />

            {/* ===== REALTOR PROFILE BANNER ===== */}
            <section className="relative overflow-hidden bg-gradient-to-br from-[#2D96A7]/5 via-white to-white">
              <div
                className="absolute inset-0 opacity-[0.03]"
                style={{
                  backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fillRule='evenodd'%3E%3Cg fill='%232D96A7' fillOpacity='0.2'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
                }}
              />

              <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 md:py-16">
                <div className="bg-white border border-gray-200 rounded-2xl shadow-sm overflow-hidden">
                  {/* Cover */}
                  <div className="relative h-36 md:h-48 bg-gradient-to-r from-[#2D96A7]/20 via-[#2D96A7]/5 to-white" />

                  <div className="relative px-6 md:px-10 pb-8 -mt-16 md:-mt-20">
                    <div className="flex flex-col md:flex-row md:items-end gap-6">
                      {/* Avatar */}
                      <div className="relative flex-shrink-0">
                        <div className="w-28 h-28 md:w-36 md:h-36 rounded-2xl border-4 border-white overflow-hidden shadow-lg bg-gray-100">
                          {realtorData.photo ? (
                            <img
                              src={realtorData.photo}
                              alt={`${realtorName}`}
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center">
                              {/* FIX: guard against empty first/last name (single-word
                              names produce an empty last_name) so we never index
                              into an empty string and print "undefined". */}
                              <span className="text-3xl font-bold text-gray-400">
                                {(realtorName || "?").toUpperCase()}
                                {/* {realtorData.last_name?.[0]?.toUpperCase() || ''} */}
                              </span>
                            </div>
                          )}
                        </div>
                        {isVerifiedRealtor && (
                          <div className="absolute -bottom-1 -right-1 w-8 h-8 bg-[#2D96A7] rounded-lg flex items-center justify-center shadow-lg shadow-[#2D96A7]/30">
                            <IconCircleCheck className="w-4 h-4 text-white" />
                          </div>
                        )}
                      </div>

                      {/* Info */}
                      <div className="flex-1 min-w-0 pb-1">
                        <div className="flex flex-wrap items-center gap-3 mb-1">
                          <h1 className="text-2xl md:text-3xl font-semibold text-gray-900 tracking-tight">
                            {realtorName}
                          </h1>
                          {isVerifiedRealtor && (
                            <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-[#2D96A7]/10 text-[#2D96A7] border border-[#2D96A7]/30">
                              <IconCircleCheck className="w-3 h-3" />
                              Verified
                            </span>
                          )}
                          {realtorData.is_partner && (
                            <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-blue-50 text-blue-600 border border-blue-200">
                              <IconShieldCheck className="w-3 h-3" />
                              Partner
                            </span>
                          )}
                        </div>
                        <p className="text-gray-500 text-sm md:text-base mb-3">
                          {realtorName}
                        </p>
                        <div className="flex flex-wrap items-center gap-4 text-sm text-gray-400">
                          {realtorData.state && (
                            <div className="flex items-center gap-1.5">
                              <IconMapPin className="w-4 h-4 text-[#2D96A7]" />
                              {realtorData.state}
                            </div>
                          )}
                          <div className="flex items-center gap-1.5">
                            <IconCalendar className="w-4 h-4 text-[#2D96A7]" />
                            Joined {formatJoinDate(realtorData.join_date)}
                          </div>
                          <span className="text-xs uppercase tracking-wider text-gray-400 bg-gray-50 px-2 py-0.5 rounded font-medium">
                            {realtorData.plan}
                          </span>
                        </div>
                      </div>

                      {/* Actions */}
                      <div className="flex flex-col sm:flex-row gap-3 pb-1 flex-shrink-0">
                        <button
                          onClick={() => setShowContactModal(true)}
                          className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-[#2D96A7] hover:bg-[#2D96A7]/90 text-white text-sm font-semibold rounded-xl transition-all duration-300 shadow-sm hover:shadow-md"
                        >
                          <IconMessageCircle className="w-4 h-4" />
                          Contact
                        </button>
                        <button
                          onClick={handleShare}
                          className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-white hover:bg-gray-50 text-gray-700 text-sm font-semibold rounded-xl border border-gray-200 hover:border-gray-300 transition-all duration-300"
                        >
                          <IconShare className="w-4 h-4" />
                          Share
                        </button>
                      </div>
                    </div>

                    {/* Bio */}
                    {realtorData.bio && (
                      <p className="mt-6 text-gray-500 text-sm leading-relaxed max-w-3xl">
                        {realtorData.bio}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            </section>

            {/* ===== PROPERTIES SECTION ===== */}
            <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 md:py-16">
              <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-8">
                <div>
                  <h2 className="text-2xl md:text-3xl font-semibold text-gray-900 tracking-tight">
                    Properties by {realtorName}
                  </h2>
                  <p className="text-gray-400 text-sm mt-1">
                    Showing {properties.length} of {totalCount} properties
                  </p>
                </div>
              </div>

              {/* Filter Tabs */}
              <div className="mb-8">
                <div className="flex flex-wrap gap-2">
                  {availableCategories.map((cat) => {
                    const isActive = activeFilter === cat.key;
                    const count = categoryCounts[cat.key] || 0;

                    return (
                      <button
                        key={cat.key}
                        onClick={() => setActiveFilter(cat.key)}
                        className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium transition-all duration-300 ${
                          isActive
                            ? "bg-[#2D96A7] text-white shadow-sm"
                            : "bg-white text-gray-500 border border-gray-200 hover:bg-gray-50 hover:text-gray-700 hover:border-gray-300"
                        }`}
                      >
                        <cat.Icon className="w-4 h-4" />
                        {cat.label}
                        <span
                          className={`text-xs px-1.5 py-0.5 rounded-md ${isActive ? "bg-white/20 text-white" : "bg-gray-100 text-gray-400"}`}
                        >
                          {count}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Property Grid */}
              {loading ? (
                <div className="flex justify-center py-20">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#2D96A7]" />
                </div>
              ) : propertiesError ? (
                <div className="text-center py-20 bg-white border border-gray-200 rounded-2xl">
                  <IconBuilding className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-gray-500 mb-1">
                    Unable to load properties
                  </h3>
                  <p className="text-sm text-gray-400 mb-4">
                    {propertiesError}
                  </p>
                  <button
                    onClick={() => fetchProperties(1)}
                    className="inline-flex items-center gap-1.5 text-sm font-medium text-[#2D96A7] hover:text-[#2D96A7]/80 transition-colors"
                  >
                    Try again
                    <IconArrowRight className="w-4 h-4" />
                  </button>
                </div>
              ) : filteredProperties.length > 0 ? (
                <div
                  ref={propertiesGridRef}
                  className="p-2 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 justify-items-center gap-5 w-full"
                >
                  {filteredProperties.map((property) => (
                    // console.log(property)
                    <PropertyListingCard
                      key={property.id}
                      property={property}
                      onCardClick={(p) =>
                        router.push(
                          buildPropertyDetailsUrl(
                            p.slug || p.id,
                            {
                              key: "realtor",
                              id: realtorData?.slug || realtorData?.id,
                            },
                            realtorData?.slug || realtorData?.id,
                          ),
                        )
                      }
                    />
                  ))}
                </div>
              ) : (
                <div className="text-center py-20 bg-white border border-gray-200 rounded-2xl">
                  <IconBuilding className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-gray-500 mb-1">
                    No properties found
                  </h3>
                  <p className="text-sm text-gray-400 mb-4">
                    This realtor doesn&rsquo;t have any {activeFilter}{" "}
                    properties yet.
                  </p>
                  <button
                    onClick={() => setActiveFilter("all")}
                    className="inline-flex items-center gap-1.5 text-sm font-medium text-[#2D96A7] hover:text-[#2D96A7]/80 transition-colors"
                  >
                    View all properties
                    <IconArrowRight className="w-4 h-4" />
                  </button>
                </div>
              )}

              <LoadMore
                hasMore={properties.length < totalCount}
                isLoading={loading || isLoadingMore}
                onLoadMore={handleLoadMore}
                loadedCount={properties.length}
                totalCount={totalCount}
                pageSize={pageSize}
                contentRef={propertiesGridRef as React.RefObject<HTMLElement>}
                className="w-full"
              />
            </section>

            {/* ===== CTA BANNER ===== */}
            <PropertyCtaBanner />
          </div>

          {/* ===== CONTACT MODAL ===== */}
          {showContactModal && (
            <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
              <div
                className="absolute inset-0 bg-black/50 backdrop-blur-sm"
                onClick={() => setShowContactModal(false)}
              />
              <div className="relative w-full max-w-md bg-white border border-gray-200 rounded-2xl shadow-xl overflow-hidden">
                <div className="relative px-6 pt-6 pb-4 border-b border-gray-100">
                  <div className="flex items-center justify-between">
                    <h3 className="text-lg font-semibold text-gray-900">
                      Contact {realtorData.first_name}
                    </h3>
                    <button
                      onClick={() => setShowContactModal(false)}
                      className="w-8 h-8 rounded-lg bg-gray-50 hover:bg-gray-100 flex items-center justify-center text-gray-400 hover:text-gray-600 transition-all duration-200"
                    >
                      <IconX className="w-4 h-4" />
                    </button>
                  </div>
                </div>

                <div className="px-6 py-6 space-y-3">
                  <a
                    href={`tel:${realtorData.phone}`}
                    className="flex items-center gap-4 p-4 rounded-xl bg-gray-50 hover:bg-[#2D96A7]/5 border border-gray-100 hover:border-[#2D96A7]/20 transition-all duration-200 group"
                  >
                    <div className="w-11 h-11 rounded-xl bg-[#2D96A7]/10 flex items-center justify-center flex-shrink-0 group-hover:bg-[#2D96A7]/20 transition-colors">
                      <IconPhone className="w-5 h-5 text-[#2D96A7]" />
                    </div>
                    <div className="min-w-0">
                      <div className="text-xs text-gray-400 uppercase tracking-wider mb-0.5">
                        Phone
                      </div>
                      <div className="text-sm font-medium text-gray-800 truncate">
                        {formatPhoneDisplay(realtorData.phone)}
                      </div>
                    </div>
                  </a>

                  <a
                    href={formatWhatsAppLink(realtorData.phone)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-4 p-4 rounded-xl bg-gray-50 hover:bg-green-50 border border-gray-100 hover:border-green-200 transition-all duration-200 group"
                  >
                    <div className="w-11 h-11 rounded-xl bg-green-100 flex items-center justify-center flex-shrink-0 group-hover:bg-green-200 transition-colors">
                      <WhatsAppIcon className="w-5 h-5 text-green-600" />
                    </div>
                    <div className="min-w-0">
                      <div className="text-xs text-gray-400 uppercase tracking-wider mb-0.5">
                        WhatsApp
                      </div>
                      <div className="text-sm font-medium text-gray-800 truncate">
                        {formatPhoneDisplay(realtorData.phone)}
                      </div>
                    </div>
                  </a>

                  <a
                    href={`mailto:${realtorData.email}`}
                    className="flex items-center gap-4 p-4 rounded-xl bg-gray-50 hover:bg-blue-50 border border-gray-100 hover:border-blue-200 transition-all duration-200 group"
                  >
                    <div className="w-11 h-11 rounded-xl bg-blue-100 flex items-center justify-center flex-shrink-0 group-hover:bg-blue-200 transition-colors">
                      <IconMail className="w-5 h-5 text-blue-500" />
                    </div>
                    <div className="min-w-0">
                      <div className="text-xs text-gray-400 uppercase tracking-wider mb-0.5">
                        Email
                      </div>
                      <div className="text-sm font-medium text-gray-800 truncate">
                        {realtorData.email}
                      </div>
                    </div>
                  </a>
                </div>

                <div className="px-6 py-4 bg-gray-50 border-t border-gray-100">
                  <p className="text-xs text-gray-400 text-center">
                    By contacting this realtor, you agree to our Terms of
                    Service
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* ===== REVIEW DRAWER ===== */}
          {typeof realtorid === "string" && (
            <ReviewDrawer
              realtorDataId={realtorid}
              realtorDataName={realtorName}
              opened={showReviewDrawer}
              onClose={() => setShowReviewDrawer(false)}
            />
          )}
        </div>
      </HomeWrapper>
    </>
  );
};

export default RealtorProfilePage;
